<script lang="ts">
  import clsx from 'clsx'
  import { onMount, untrack } from 'svelte'
  import type { MediaPropertiesType } from '../utils/Media'
  import type HlsEngine from '../modules/HlsEngine'
  import type { HlsEvent } from '../modules/HlsEngine'
  import { HlsjsImpl } from '../modules/HlsjsImpl'
  import captionsState from '../stores/captions.svelte'
  import chaptersStore, { type Cue } from '../stores/chapters.svelte'
  import componentAttributesStore from '../stores/componentAttributes.svelte'
  import videoStore from '../stores/video.svelte'
  import translationsStore from '../stores/translations.svelte'
  import { HLS_EVENTS, VIDEO_TAG_EVENTS } from '../types/video'
  import Analytics from '../modules/Analytics'
  import ChromecastController from './ChromecastController.svelte'
  import { LIVESTREAM_STATUS } from '../const/livestream'

  type WebkitVideoFullscreenProps = {
    webkitSupportsFullscreen?: boolean
    webkitEnterFullscreen?: () => void
  }

  let videoRef: (HTMLVideoElement & WebkitVideoFullscreenProps) | null = null
  let chromecastControllerRef: ChromecastController | null = null
  let hlsEngine: HlsEngine | null = null
  let analytics: Analytics | null = null
  let previousManifestUrl: string = ''
  let otherEventsListeners: { type: string; listener: EventListener }[] = []

  type CUSTOM_EVENT =
    | 'firstplay'
    | 'airplayConnected'
    | 'airplayDisconnected'
    | 'chromecastConnected'
    | 'chromecastDisconnected'
    | 'controlsdisabled'
    | 'controlsenabled'
    | 'fullscreenchange'
    | 'mouseenter'
    | 'mouseleave'
    | 'useractive'
    | 'userinactive'
    | 'qualitychange'
    | 'playerresize'
    | 'error'
    | 'resize' //TODO

  let bufferingTimeout: number | null = $state(null)

  onMount(() => {
    if (videoRef) {
      analytics = new Analytics(videoRef)
      hlsEngine = new HlsjsImpl(videoRef)

      hlsEngine.addEventListener('videotypechanged', (e) => {
        if (videoStore.state.mediaDescriptor?.mediaType === 'live') {
          if (
            /* If video if already not live anymore when reloaded Or if live turns off when user is on the edge */
            (!videoStore.state.videoType || videoStore.isLive()) &&
            !e.detail.isLive
          ) {
            videoStore.state.liveStatus = LIVESTREAM_STATUS.ENDED
          }
          if (!videoStore.state.videoType && e.detail.isLive) {
            videoStore.state.isBuffering = true
          }
          videoStore.state.liveSyncPosition =
            hlsEngine?.getLiveSyncPosition() || 0
          videoStore.state.dvrDuration = e.detail.dvrDuration
        }
        if (videoStore.state.videoType !== e.detail.type) {
          videoStore.state.videoType = e.detail.type
        }
      })
      hlsEngine.addEventListener('hlsManifestLoaded', (e) => {
        if (videoStore.state.mediaDescriptor?.mediaType === 'live') {
          videoStore.state.liveStatus = LIVESTREAM_STATUS.STREAMING
        }
        videoStore.state.qualityLevels = hlsEngine?.getLevels() || []
      })
      hlsEngine.addEventListener('hlsManifestParsed', (e) => {
        if (componentAttributesStore.state.maxQuality) {
          hlsEngine?.setMaxQuality(componentAttributesStore.state.maxQuality)
        }
      })
      hlsEngine.addEventListener('hlsError', (e) => {
        switch (e.detail.details) {
          case 'manifestLoadError':
            onManifestLoadError()
            break
          case 'networkError':
            videoStore.state.errorMessage =
              translationsStore.state.network_error
            break
          case 'mediaError':
            videoStore.state.errorMessage = translationsStore.state.media_error
            break
          case 'bufferStalledError':
            videoStore.state.isBuffering = true
            break
          case 'otherError':
            videoStore.state.errorMessage = translationsStore.state.other_error
            break
          // default:
          //   videoStore.setErrorMessage('Unknown error ' + e.detail.details)
        }
      })
      hlsEngine.addEventListener('hlsLevelSwitched', (e) => {
        //console.log(e, hlsEngine?.getLevels()[e.detail.level])
        const level = hlsEngine?.getLevels()?.[e.detail?.level]
        if (level) {
          dispatchOtherEvent('qualitychange', {
            resolution: {
              width: level.width,
              height: level.height,
            },
          })
        }
      })
      hlsEngine.addEventListener('subtitletracksloaded', (e) => {
        Array.from(videoRef?.textTracks || []).forEach((a) =>
          a.addEventListener('cuechange', (e) => {
            const textTrack: TextTrack = e.target as TextTrack
            const activeCue = textTrack.activeCues?.[0]
            captionsState.state.activeCaptionCue = (activeCue as any)?.text
          }),
        )
        captionsState.state.subtitleTracks = e.detail
      })
      hlsEngine.addEventListener('hlsFragBuffered', (e) => {
        hideBufferingScreen()
      })

      videoStore.state.isAirplaySupported = !!(videoRef as any)
        .webkitShowPlaybackTargetPicker

      videoStore.state.isPicureInPictureSupported =
        !!document.pictureInPictureEnabled

      videoRef.addEventListener(
        'webkitcurrentplaybacktargetiswirelesschanged',
        (e: any) =>
          dispatchOtherEvent(
            e.currentTarget.webkitCurrentPlaybackTargetIsWireless
              ? 'airplayConnected'
              : 'airplayDisconnected',
          ),
      )
    }
  })

  $effect(() => {
    if (videoStore.state.videoWidth) {
      if (
        videoStore.isLiveStream() &&
        !videoStore.state.hasPlaybackStarted &&
        !videoStore.state.isPlaying
      ) {
        setMuted(true)
        play()
      }
    }
  })

  $effect(() => {
    if (videoStore.state.isPlaying && !videoStore.state.hasPlaybackStarted) {
      videoStore.state.hasPlaybackStarted = true
      hlsEngine?.adaptPlayingBuffer()
    }
  })

  $effect(() => {
    hlsEngine?.setSubtitleTrack(
      captionsState.state.selectedSubtitleTrackId || 0,
    )
  })

  $effect(() => {
    captionsState.setCaptionsEnabled(
      componentAttributesStore.state.showCaptions,
    )
  })

  $effect(() => {
    const hasPlaybackstarted = videoStore.state.hasPlaybackStarted
    untrack(() => {
      if (hasPlaybackstarted) {
        dispatchOtherEvent('firstplay')
      }
    })
  })

  $effect(() => {
    const playerWidth = videoStore.state.playerWidth
    const playerHeight = videoStore.state.playerHeight
    if (playerWidth || playerHeight) {
      untrack(() => {
        dispatchOtherEvent('playerresize', {
          width: playerWidth,
          height: playerHeight,
        })
      })
    }
  })

  $effect(() => {
    const errorMessage = videoStore.state.errorMessage
    untrack(() => {
      dispatchOtherEvent('error', { message: errorMessage })
    })
  })

  $effect(() => {
    const isUserActive = videoStore.state.isUserActive
    untrack(() => {
      dispatchOtherEvent(isUserActive ? 'useractive' : 'userinactive')
    })
  })

  $effect(() => {
    const mouseOver = videoStore.state.mouseOver
    untrack(() => {
      dispatchOtherEvent(mouseOver ? 'mouseenter' : 'mouseleave')
    })
  })

  $effect(() => {
    const isFullscreen = videoStore.state.isFullscreen
    untrack(() => {
      dispatchOtherEvent('fullscreenchange', { isFullscreen: isFullscreen })
    })
  })

  $effect(() => {
    const isChromecasting = videoStore.state.isChromecasting
    untrack(() => {
      dispatchOtherEvent(
        isChromecasting ? 'chromecastConnected' : 'chromecastDisconnected',
      )
    })
  })

  $effect(() => {
    const controlsHidden = componentAttributesStore.state.hideControls
    untrack(() => {
      dispatchOtherEvent(
        controlsHidden ? 'controlsdisabled' : 'controlsenabled',
      )
    })
  })

  $effect(() =>
    hlsEngine?.setMaxQuality(componentAttributesStore.state.maxQuality),
  )
  $effect(() =>
    hlsEngine?.forceQuality(
      componentAttributesStore.state.forcedQualityLevel || null,
    ),
  )

  $effect(() => {
    if (
      videoStore.state.mediaDescriptor?.manifestUrl &&
      videoStore.state.mediaDescriptor?.manifestUrl !== previousManifestUrl
    ) {
      previousManifestUrl = videoStore.state.mediaDescriptor?.manifestUrl
      loadMedia(videoStore.state.mediaDescriptor!)
    }
  })

  $effect(() => {
    if (
      videoStore.state.mediaDescriptor?.mediaId &&
      componentAttributesStore.state.collectorDomain
    ) {
      analytics?.observeMedia(
        videoStore.state.mediaDescriptor?.mediaId,
        componentAttributesStore.state.collectorDomain,
      )
    }
  })

  $effect(() => {
    videoStore.forceMobileLayout(
      componentAttributesStore.state.forceMobileLayout,
    )
  })

  $effect(() => {
    if (videoStore.state.isChromecasting) {
      videoRef?.pause()
    }
  })

  function onManifestLoadError() {
    if (videoStore.state.mediaDescriptor?.mediaType === 'live') {
      videoStore.state.liveStatus = LIVESTREAM_STATUS.WAITING
      setTimeout(() => {
        videoStore.state.mediaDescriptor?.manifestUrl &&
          hlsEngine?.load(videoStore.state.mediaDescriptor?.manifestUrl)
      }, 5000)
    } else {
      videoStore.state.errorMessage =
        translationsStore.state.manifest_load_error
    }
  }

  function loadMedia(mediaDescriptor: MediaPropertiesType) {
    if (videoStore.state.liveStatus !== LIVESTREAM_STATUS.LOADING) {
      videoStore.state.liveStatus = LIVESTREAM_STATUS.LOADING
    }
    hlsEngine?.load(mediaDescriptor.manifestUrl)

    //setCurrentTime(0)
    pause()
    videoStore.resetPlayer()
    chaptersStore.reset()
    captionsState.reset()

    if (videoStore.state.isChromecasting) {
      chromecastControllerRef?.loadMedia(true)
    }
  }

  export function addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) {
    if (VIDEO_TAG_EVENTS.includes(type)) {
      videoRef?.addEventListener(type, listener, options)
    } else if (HLS_EVENTS.includes(type)) {
      hlsEngine?.addEventListener(type as HlsEvent, (e) => {
        if (typeof listener === 'function') {
          listener(e as any)
        } else if (listener && typeof listener.handleEvent === 'function') {
          listener.handleEvent(e as any)
        }
      })
    } else {
      otherEventsListeners.push({ type, listener: listener as EventListener })
    }
  }

  function dispatchOtherEvent(type: CUSTOM_EVENT, details?: any) {
    const listeners = otherEventsListeners.filter((a) => a.type === type)
    listeners.forEach((a) =>
      a.listener(
        details
          ? new CustomEvent(type, { detail: details })
          : new CustomEvent(type),
      ),
    )
  }

  export function setMaxQuality(height: number) {
    hlsEngine?.setMaxQuality(height)
  }

  export function toggleFullscreen() {
    if (videoStore.state.isFullscreen) {
      exitFullscreen()
    } else {
      requestFullscreen()
    }
  }

  export function requestFullscreen() {
    try {
      const container = videoStore.state.containerElement
      if (videoStore.state.videoController && container) {
        container.requestFullscreen({
          navigationUI: 'hide',
        })
      }
    } catch (error) {
      if (
        videoRef &&
        videoRef.webkitSupportsFullscreen &&
        videoRef.webkitEnterFullscreen
      ) {
        videoRef.webkitEnterFullscreen()
        videoStore.state.isWebkitFullscreen = true
      } else {
        console.error('Error on activating Fullscreen:', error)
      }
    }
  }

  export function exitFullscreen() {
    document.exitFullscreen()
  }

  export function toggleMute() {
    if (videoStore.state.isChromecasting) {
      chromecastControllerRef?.toggleMute()
    }
    if (videoRef) {
      const isActuallyMuted = videoRef.muted
      if (videoRef.volume === 0) {
        setVolume(1)
      }
      videoRef.muted = !isActuallyMuted
    }
  }

  export function setMuted(muted: boolean) {
    if (videoRef) {
      videoRef.muted = muted
    }
  }

  export function setVolume(volume: number) {
    const newVolume = volume < 0 ? 0 : volume > 1 ? 1 : volume
    if (videoStore.state.isChromecasting) {
      chromecastControllerRef?.setVolume(newVolume)
    }
    if (videoRef) {
      videoRef.volume = newVolume
    }
    if (volume > 0) {
      setMuted(false)
    }
    if (volume === 0) {
      setMuted(true)
    }
  }

  export function setCurrentTime(time: number) {
    if (videoStore.state.isChromecasting) {
      chromecastControllerRef?.setCurrentTime(time)
    } else if (videoRef) {
      videoRef.currentTime = time
    }
  }

  export function downloadMp4(filename?: string) {
    let downloadUrl = videoStore.state.mediaDescriptor?.mp4Url
    if (!downloadUrl) {
      return
    }

    const createLink = (url: string, filename: string) => {
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.target = '_blank'
      a.click()
    }

    if (filename) {
      fetch(downloadUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob)
          createLink(url, filename)
          window.URL.revokeObjectURL(url)
        })
      return
    }
    createLink(
      downloadUrl + (downloadUrl.includes('?') ? '&' : '?') + 'dl=1',
      'video.mp4',
    )
  }

  export function showSubtitles() {
    captionsState.state.subtitleEnabled = true
  }

  export function hideSubtitles() {
    captionsState.state.subtitleEnabled = false
  }

  export function toggleSubtitles() {
    captionsState.setCaptionsEnabled(!captionsState.state.subtitleEnabled)
  }

  export function togglePlayPause() {
    videoStore.state.isChromecasting
      ? videoStore.state.isEnded || videoStore.state.currentTime === 0
        ? chromecastControllerRef?.loadMedia(true)
        : chromecastControllerRef?.togglePlayPause()
      : videoRef?.paused
        ? play()
        : pause()
  }

  export function seekForward() {
    seek(20)
  }

  export function seekBackward() {
    seek(-10)
  }

  export function seek(seconds: number) {
    if (videoStore.state.isChromecasting) {
      chromecastControllerRef?.seek(seconds)
    } else if (videoRef) {
      videoRef.currentTime += seconds
    }
  }

  export function increaseVolume(step: number) {
    if (videoRef) {
      const newVolume = Math.max(0, Math.min(videoRef.volume + step, 1))
      if (videoStore.state.isChromecasting) {
        chromecastControllerRef?.setVolume(newVolume)
      }
      videoRef.volume = newVolume
    }
  }

  export function play() {
    if (videoRef) {
      if (videoStore.isLiveStream()) {
        if (!videoStore.state.hasPlaybackStarted) {
          videoRef.currentTime =
            hlsEngine?.getLiveSyncPosition() || videoRef.duration
        } else {
          const minTime =
            videoStore.state.liveSyncPosition - videoStore.state.dvrDuration
          if (videoRef.currentTime < minTime) {
            videoRef.currentTime = minTime
          }
        }
      } else {
        if (videoStore.state.isEnded && videoRef.currentTime !== 0) {
          videoRef.currentTime = 0
        }
      }
    }

    return videoRef?.play()
  }

  export function pause() {
    videoRef?.pause()
  }

  export async function togglePip() {
    if (videoStore.state.isPicureInPictureSupported) {
      try {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture()
        } else {
          await videoRef?.requestPictureInPicture()
        }
      } catch (error) {
        console.error('Error on activating PiP:', error)
      }
    }
  }

  export async function requestPictureInPicture() {
    if (videoStore.state.isPicureInPictureSupported) {
      try {
        await videoRef?.requestPictureInPicture()
      } catch (error) {
        console.error('Error on activating PiP:', error)
      }
    }
  }

  export async function exitPictureInPicture() {
    if (videoStore.state.isPicureInPictureSupported) {
      try {
        await document.exitPictureInPicture()
      } catch (error) {
        console.error('Error on deactivating PiP:', error)
      }
    }
  }

  async function onLoadedMetadata() {
    if (componentAttributesStore.state.sequenceStart > 0) {
      setCurrentTime(componentAttributesStore.state.sequenceStart)
    }
    if (componentAttributesStore.state.autoplay) {
      try {
        await play()
      } catch (error) {
        setMuted(true)
        await play()
      }
    }
  }

  function fixOverlappingChapters(chapters: Cue[]) {
    if (!chapters) {
      return []
    }
    const sortedChapters = [...chapters].sort((a, b) => a.start - b.start)
    for (let i = 0; i < sortedChapters.length - 1; i++) {
      const currentChapter = chapters[i]
      const nextChapter = chapters[i + 1]
      if (currentChapter.end > nextChapter.start) {
        currentChapter.end = nextChapter.start
      }
    }
    return sortedChapters.filter((c) => c.end > c.start)
  }

  /*async function loadChapters() {
    async function getTrackCues(track: TextTrack): Promise<Cue[]> {
      if (!track.cues) {
        await new Promise((resolve) => {
          setTimeout(() => {
            resolve(null)
          }, 200)
        })
        return getTrackCues(track)
      }
      return Array.from(track.cues).map((a) => ({
        text: (a as any).text,
        start: a.startTime,
        end: a.endTime,
        id: (a as any).id,
      }))
    }
    videoRef?.textTracks.addEventListener(
      'addtrack',
      (e) => {
        const tracks = e.currentTarget as TextTrackList
        for (let i = 0; i < tracks.length; i++) {
          const track = tracks[i]
          if (track.kind !== 'chapters') {
            continue
          }
          getTrackCues(track).then(
            (a) => (chaptersStore.state.cues = fixOverlappingChapters(a)),
          )
          track.mode = 'hidden'
        }
      },
      false,
    )
    const chaptersEntries = videoStore.state.mediaDescriptor?.chaptersEntries
    for (let chapterEntry of chaptersEntries || []) {
      const chapterUrl =
        videoStore.state.mediaDescriptor?.getChapterUrl(chapterEntry)
      const chapterElement = document.createElement('track')
      chapterElement.kind = 'chapters'
      chapterElement.src = chapterUrl!
      chapterElement.id = chapterEntry
      chapterElement.srclang = chapterEntry
      videoRef?.appendChild(chapterElement)
    }
  }*/

  async function onEnded() {
    if (componentAttributesStore.state.loop && !videoStore.isLiveStream()) {
      setCurrentTime(0)
      await play()
    }
    if (videoStore.isLiveStream()) {
      videoStore.state.liveStatus = LIVESTREAM_STATUS.ENDED
    }
  }

  export function toggleChromecast() {
    chromecastControllerRef?.toggleCast()
  }

  export function toggleAirplay() {
    if (videoRef) {
      ;(videoRef as any).webkitShowPlaybackTargetPicker()
    }
  }

  function onTimeUpdate() {
    if (
      componentAttributesStore.state.sequenceEnd > 0 &&
      videoRef?.currentTime &&
      videoRef?.currentTime >= componentAttributesStore.state.sequenceEnd &&
      videoStore.state.currentTime <= componentAttributesStore.state.sequenceEnd
    ) {
      videoRef?.pause()
    }
    videoStore.state.currentTime = videoRef?.currentTime || 0
    //videoStore.state.liveSyncPosition = hlsEngine?.getLiveSyncPosition() || 0
  }

  function onStateChange() {
    videoStore.state.isPlaying = !videoRef?.paused
  }

  function showBufferingScreen() {
    if (bufferingTimeout) {
      clearTimeout(bufferingTimeout)
      bufferingTimeout = null
    }
    bufferingTimeout = setTimeout(() => {
      videoStore.state.isBuffering = true
      bufferingTimeout = null
    }, 300)
  }

  function hideBufferingScreen() {
    if (bufferingTimeout) {
      clearTimeout(bufferingTimeout)
      bufferingTimeout = null
    }
    if (videoStore.state.isBuffering) {
      videoStore.state.isBuffering = false
    }
  }
</script>

<div class="flex w-full h-full bg-black">
  <!-- svelte-ignore a11y_media_has_caption -->
  <video
    crossorigin="anonymous"
    bind:this={videoRef}
    bind:duration={videoStore.state.duration}
    bind:videoWidth={videoStore.state.videoWidth}
    bind:videoHeight={videoStore.state.videoHeight}
    bind:playbackRate={componentAttributesStore.state.playbackRate}
    bind:muted={componentAttributesStore.state.muted}
    bind:volume={componentAttributesStore.state.volume}
    bind:ended={videoStore.state.isEnded}
    onplay={() => onStateChange()}
    onpause={() => onStateChange()}
    ontimeupdate={() => onTimeUpdate()}
    onloadedmetadata={() => onLoadedMetadata()}
    onloadeddata={() => {
      /*loadChapters()*/
    }}
    onended={() => onEnded()}
    onseeking={() => showBufferingScreen()}
    onseeked={() => hideBufferingScreen()}
    class={clsx('w-full', videoStore.state.isChromecasting && 'hidden')}
    playsinline
  ></video>

  <ChromecastController
    bind:this={chromecastControllerRef}
    manifest={videoStore.state.mediaDescriptor?.manifestUrl || ''}
  />
</div>
